// Fichier : EditTab.tsx
// Ce fichier contient la définition et l'implémentation du composant pour modifier une Tab existante.
// Il utilise React Hook Form et Zod pour la validation de formulaire, RTK Query Pour la gestion des mutations , et React Roastify pour Notifications .

import React, { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { IoReturnDownBackSharp } from "react-icons/io5";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetTabByIdQuery,
  useUpdateTabMutation,
} from "@/state/usersApiSlice";
import styles from "./EditTab.module.css";
 
import { tabSchema } from "@/zod Schemas/zodTabSchema";
import { toast } from "react-toastify";

// Définition du type de données du formulaire basé sur le schéma Zod
type TabFormData = z.infer<typeof tabSchema>;

// Fonction principale du composant EditTab
const EditTab: React.FC = () => {
  // Fonction de notification pour afficher un message de succès
  const notifyUpdateTabSuccess = () =>
    toast.success("Tab Updated Successfully", {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });

  const { id } = useParams(); // Hook pour récupérer les paramètres de l'URL
  const navigate = useNavigate(); // Hook pour la navigation donné par React Router
  const { data: tab, isLoading, isError } = useGetTabByIdQuery(id); // Requête pour obtenir les détails d'une Tab par son ID , on en aura besoin pour rémplir les champs avec le chargement de la page.
  const [
    updateTab,
    { isLoading: isUpdating, isSuccess, isError: updateError },
  ] = useUpdateTabMutation(); // Mutation pour mettre à jour une Tab

  // Hook pour gérer le formulaire avec validation Zod
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TabFormData>({
    resolver: zodResolver(tabSchema),
    defaultValues: {
      id: "",
      tab_name: "",
      link: "",
      link_type: "",
    }, // Valeurs par défaut du formulaire
  });

  useEffect(() => {
    // useEffect pour remplir les champs du formulaire avec les données de l'onglet ,
    if (tab) {
      reset({
        id: tab.id,
        tab_name: tab.tab_name,
        link: tab.link,
        link_type: tab.link_type,
      });
    }
  }, [tab, reset]); // la dependance "tab" va déclencher ce useEffect quand la valeur Tab est rempli.

  // Fonction appelée lors de la soumission du formulaire
  const onSubmit: SubmitHandler<TabFormData> = async (data) => {
    try {
      await updateTab({ id, updatedInfos: data }).unwrap(); // Si la mise à jour réussit, afficher une notification et rediriger
      notifyUpdateTabSuccess();
      navigate("/tabs/all");
    } catch (error) {
      console.error("Failed to update tab:", error);
      alert("Failed to update tab. Please try again.");
    }
  };

  // Affichage conditionnel en fonction de l'état de la requête
  if (isLoading) return <p>Loading...</p>; // Afficher un message de chargement
  if (isError) return <p>Error loading tab details</p>; // Afficher un message d'erreur

  return (
    <div className={styles.container}>
      <div className={styles.formContainer}>
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          {/* Groupe de champs de formulaire pour l'ID */}
          <div className={styles.formGroup}>
            <label className={styles.label}>ID</label>
            <input
              type="text"
              {...register("id")}
              name="id"
              className={styles.input}
              disabled
            />
          </div>

          {/* Groupe de champs de formulaire pour le nom de l'onglet */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Name</label>
            <input
              {...register("tab_name")}
              type="text"
              name="tab_name"
              className={styles.input}
            />
            {errors.tab_name && (
              <p className={styles.error}>{errors.tab_name.message}</p>
            )}
          </div>

          {/* Groupe de champs de formulaire pour le lien */}
          <div className={styles.formGroup}>
            <label className={styles.label}>LINK</label>
            <input
              {...register("link")}
              type="text"
              name="link"
              className={styles.input}
            />
            {errors.link && (
              <p className={styles.error}>{errors.link.message}</p>
            )}
          </div>

          {/* Groupe de champs de formulaire pour le type de lien */}
          <div className={styles.formGroup}>
            <label htmlFor="link_type" className={styles.label}>
              Link Type:
            </label>
            <select
              id="link_type"
              {...register("link_type")}
              name="link_type"
              required
              className={`${styles.input} ${styles.inputDisabled}`}
            >
              <option value="wordpress">WordPress</option>
              <option value="chatwoot">Chatwoot</option>
              <option value="fcm">FCM</option>
              <option value="transactions">Transactions</option>
            </select>
          </div>

          {/* Groupe de boutons pour soumettre ou revenir à la liste des onglets */}
          <div className={styles.buttonGroup}>
            <button
              type="submit"
              disabled={isUpdating}
              className={styles.buttonPrimary}
            >
              {isUpdating ? "Updating..." : "Update Tab"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/tabs")}
              className={styles.buttonSecondary}
            >
              Go back to Tabs List <IoReturnDownBackSharp />
            </button>
          </div>

          {/* Messages de succès ou d'erreur */}
          {isSuccess && (
            <p className={styles.successMessage}>Tab updated successfully!</p>
          )}
          {updateError && (
            <p className={styles.errorMessage}>
              Error updating tab: {updateError}
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default EditTab;
